import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTenantBranding } from "../../../../shared/branding/TenantBrandingProvider";
import { tenantControlApi } from "../../api/tenantControlApi";
import styles from "./TenantControlPage.module.css";

const DEFAULT_LIMITS = {
  max_users: "",
  max_contacts: "",
  max_leads: "",
  max_deals: "",
  max_tasks: "",
  max_api_keys: "",
  external_api_enabled: true,
  ai_enabled: true,
};

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}

function normalizePlan(plan) {
  return String(plan?.value || plan || "").toLowerCase();
}

function getPlanCode(plan) {
  return String(plan?.code || plan?.plan || "").toLowerCase();
}

function getPlanName(plan) {
  return plan?.name || plan?.code || "—";
}

function getPlanLimits(plan) {
  return plan?.limits_json || {};
}

function limitText(value, max) {
  const current = Number(value || 0);
  if (max === null || max === undefined || max === "" || Number(max) < 0) {
    return `${current} / ∞`;
  }
  return `${current} / ${Number(max || 0)}`;
}

function parseLimit(value) {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? num : null;
}

function emptyStats(tenantId) {
  return {
    tenant_id: tenantId,
    users_count: 0,
    contacts_count: 0,
    leads_count: 0,
    deals_count: 0,
    tasks_count: 0,
    api_keys_count: 0,
    external_api_logs_count: 0,
  };
}

function translatePresetName(t, preset) {
  if (!preset) {
    return t("business_preset_generic_name", {
      defaultValue: "Generic CRM",
    });
  }

  const presetKey = String(preset?.key || "generic").trim();
  const nameKey =
    preset?.name_key ||
    (preset?.name && String(preset.name).startsWith("business_preset_")
      ? preset.name
      : `business_preset_${presetKey}_name`);

  return t(nameKey, {
    defaultValue: preset?.name || presetKey,
  });
}

export default function TenantControlPage() {
  const { t } = useTranslation();
  const { branding, isLoaded } = useTenantBranding();

  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [businessPresets, setBusinessPresets] = useState([]);
  const [presetSelections, setPresetSelections] = useState({});
  const [applyingPresetTenantId, setApplyingPresetTenantId] = useState(null);
  const [presetResult, setPresetResult] = useState(null);
  const [statsByTenant, setStatsByTenant] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [domainsTenant, setDomainsTenant] = useState(null);

  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editPlan, setEditPlan] = useState(null);

  const [form, setForm] = useState({
    name: "",
    plan_id: "",
    domain: "",
  });

  const [planForm, setPlanForm] = useState({
    name: "",
    code: "",
    description: "",
    is_active: true,
    monthly_price_cents: 0,
    currency: "EUR",
    limits_json: { ...DEFAULT_LIMITS },
  });

  const [domainForm, setDomainForm] = useState({
    domain: "",
    is_primary: true,
  });

  const isMainTenant = branding?.is_main === true;

  const loadBusinessPresets = async () => {
    try {
      const rows = await tenantControlApi.listBusinessPresets();
      const normalized = Array.isArray(rows) ? rows : [];
      setBusinessPresets(normalized);
      return normalized;
    } catch {
      setBusinessPresets([]);
      return [];
    }
  };

  const loadPlans = async () => {
    const rows = await tenantControlApi.listPlans();
    const normalized = Array.isArray(rows) ? rows : [];
    setPlans(normalized);
    return normalized;
  };

  const loadTenants = async () => {
    setLoading(true);
    try {
      await Promise.all([loadPlans(), loadBusinessPresets()]);

      const rows = await tenantControlApi.listTenants();
      const tenantRows = Array.isArray(rows) ? rows : [];
      setTenants(tenantRows);
      setPresetSelections(() => {
        const next = {};
        tenantRows.forEach((tenant) => {
          const currentPreset =
            tenant?.business_preset?.key ||
            tenant?.industry?.key ||
            "generic";

          next[tenant.id] = currentPreset;
        });
        return next;
      });

      const statsPairs = await Promise.all(
        tenantRows.map(async (tenant) => {
          try {
            const stats = await tenantControlApi.getTenantStats(tenant.id);
            return [tenant.id, { ...emptyStats(tenant.id), ...(stats || {}) }];
          } catch {
            return [tenant.id, emptyStats(tenant.id)];
          }
        })
      );

      setStatsByTenant(Object.fromEntries(statsPairs));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoaded && isMainTenant) {
      loadTenants();
    } else if (isLoaded) {
      setLoading(false);
    }
  }, [isLoaded, isMainTenant]);

  const filteredTenants = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;

    return tenants.filter((tenant) => {
      const domains = (tenant.domains || []).map((d) => d.domain).join(" ");
      const plan = tenant.subscription_plan;

      const haystack = [
        tenant.id,
        tenant.name,
        normalizePlan(tenant.plan),
        getPlanName(plan),
        getPlanCode(plan),
        domains,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [tenants, search]);

  const summary = useMemo(() => {
    return {
      total: tenants.length,
      main: tenants.filter((t) => normalizePlan(t.plan) === "main").length,
      plans: plans.length,
      presets: businessPresets.length,
      activeDomains: tenants.reduce(
        (sum, t) => sum + (t.domains || []).filter((d) => d.is_active).length,
        0
      ),
      users: Object.values(statsByTenant).reduce(
        (sum, s) => sum + Number(s?.users_count || 0),
        0
      ),
    };
  }, [tenants, plans, businessPresets, statsByTenant]);

  const resetTenantForm = () => {
    setForm({
      name: "",
      plan_id: plans[0]?.id ? String(plans[0].id) : "",
      domain: "",
    });
  };

  const resetPlanForm = () => {
    setPlanForm({
      name: "",
      code: "",
      description: "",
      is_active: true,
      monthly_price_cents: 0,
      currency: "EUR",
      limits_json: { ...DEFAULT_LIMITS },
    });
  };

  const openCreate = () => {
    setForm({
      name: "",
      plan_id: plans[0]?.id ? String(plans[0].id) : "",
      domain: "",
    });
    setCreateOpen(true);
  };

  const openEdit = (tenant) => {
    setEditTenant(tenant);
    setForm({
      name: tenant.name || "",
      plan_id: tenant.plan_id ? String(tenant.plan_id) : "",
      domain: "",
    });
  };

  const openCreatePlan = () => {
    setEditPlan(null);
    resetPlanForm();
    setPlanModalOpen(true);
  };

  const openEditPlan = (plan) => {
    setEditPlan(plan);
    const limits = getPlanLimits(plan);

    setPlanForm({
      name: plan.name || "",
      code: plan.code || "",
      description: plan.description || "",
      is_active: plan.is_active !== false,
      monthly_price_cents: Number(plan.monthly_price_cents || 0),
      currency: plan.currency || "EUR",
      limits_json: {
        ...DEFAULT_LIMITS,
        ...limits,
        max_users: limits.max_users ?? "",
        max_contacts: limits.max_contacts ?? "",
        max_leads: limits.max_leads ?? "",
        max_deals: limits.max_deals ?? "",
        max_tasks: limits.max_tasks ?? "",
        max_api_keys: limits.max_api_keys ?? "",
      },
    });

    setPlanModalOpen(true);
  };

  const closeModal = () => {
    setCreateOpen(false);
    setEditTenant(null);
    setDomainsTenant(null);
    setPlanModalOpen(false);
    setEditPlan(null);
    resetTenantForm();
    resetPlanForm();
    setDomainForm({
      domain: "",
      is_primary: true,
    });
  };

  const buildPlanPayload = () => ({
    name: planForm.name.trim(),
    code: planForm.code.trim().toLowerCase().replaceAll(" ", "_"),
    description: planForm.description.trim() || null,
    is_active: !!planForm.is_active,
    monthly_price_cents: Number(planForm.monthly_price_cents || 0),
    currency: (planForm.currency || "EUR").trim().toUpperCase(),
    limits_json: {
      max_users: parseLimit(planForm.limits_json.max_users),
      max_contacts: parseLimit(planForm.limits_json.max_contacts),
      max_leads: parseLimit(planForm.limits_json.max_leads),
      max_deals: parseLimit(planForm.limits_json.max_deals),
      max_tasks: parseLimit(planForm.limits_json.max_tasks),
      max_api_keys: parseLimit(planForm.limits_json.max_api_keys),
      external_api_enabled: !!planForm.limits_json.external_api_enabled,
      ai_enabled: !!planForm.limits_json.ai_enabled,
    },
  });

  const handleSavePlan = async () => {
    if (!planForm.name.trim() || !planForm.code.trim()) {
      alert(t("tenant_plan_name_code_required"));
      return;
    }

    setSaving(true);
    try {
      const payload = buildPlanPayload();

      if (editPlan) {
        await tenantControlApi.updatePlan(editPlan.id, payload);
      } else {
        await tenantControlApi.createPlan(payload);
      }

      closeModal();
      await loadTenants();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to save plan");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async (plan) => {
    if (plan.is_system) {
      alert(t("tenant_system_plan_delete_forbidden"));
      return;
    }

    const ok = window.confirm(
      t("tenant_delete_plan_confirm", { name: plan.name }),
    );
    if (!ok) return;

    setSaving(true);
    try {
      await tenantControlApi.deletePlan(plan.id);
      await loadTenants();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to delete plan");
    } finally {
      setSaving(false);
    }
  };

  const handlePresetChange = (tenantId, presetKey) => {
    setPresetSelections((prev) => ({
      ...prev,
      [tenantId]: presetKey,
    }));
  };

  const handleApplyBusinessPreset = async (tenant) => {
    const presetKey = presetSelections[tenant.id] || "generic";
    const preset = businessPresets.find((item) => item.key === presetKey);
    const presetName = translatePresetName(t, preset);

    const ok = window.confirm(
      t("tenant_apply_preset_confirm", {
        preset: presetName,
        tenant: tenant.name,
      }),
    );

    if (!ok) return;

    setPresetResult(null);
    setApplyingPresetTenantId(tenant.id);

    try {
      const result = await tenantControlApi.applyBusinessPreset({
        tenant_id: tenant.id,
        preset_key: presetKey,
      });

      setPresetResult(result);
      await loadTenants();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to apply business preset");
    } finally {
      setApplyingPresetTenantId(null);
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      alert(t("tenant_name_required"));
      return;
    }

    setSaving(true);
    try {
      await tenantControlApi.createTenant({
        name: form.name.trim(),
        plan_id: form.plan_id ? Number(form.plan_id) : null,
        domain: form.domain.trim() || null,
      });

      closeModal();
      await loadTenants();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to create tenant");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editTenant) return;

    if (!form.name.trim()) {
      alert(t("tenant_name_required"));
      return;
    }

    setSaving(true);
    try {
      await tenantControlApi.updateTenant(editTenant.id, {
        name: form.name.trim(),
        plan_id: form.plan_id ? Number(form.plan_id) : null,
      });

      closeModal();
      await loadTenants();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to update tenant");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tenant) => {
    const plan = normalizePlan(tenant.plan);

    if (plan === "main") {
      alert(t("tenant_main_delete_forbidden"));
      return;
    }

    const ok = window.confirm(
      t("tenant_delete_confirm", { name: tenant.name }),
    );

    if (!ok) return;

    try {
      await tenantControlApi.deleteTenant(tenant.id);
      await loadTenants();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to delete tenant");
    }
  };

  const openDomains = (tenant) => {
    setDomainsTenant(tenant);
    setDomainForm({
      domain: "",
      is_primary: true,
    });
  };

  const handleAddDomain = async () => {
    if (!domainsTenant) return;

    if (!domainForm.domain.trim()) {
      alert(t("tenant_domain_required"));
      return;
    }

    setSaving(true);
    try {
      await tenantControlApi.addDomain(domainsTenant.id, {
        domain: domainForm.domain.trim().toLowerCase(),
        is_primary: !!domainForm.is_primary,
      });

      const refreshed = await tenantControlApi.getTenant(domainsTenant.id);
      setDomainsTenant(refreshed);
      setDomainForm({
        domain: "",
        is_primary: true,
      });
      await loadTenants();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to add domain");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDomain = async (domain) => {
    const ok = window.confirm(
      t("tenant_delete_domain_confirm", { domain: domain.domain }),
    );
    if (!ok) return;

    setSaving(true);
    try {
      await tenantControlApi.deleteDomain(domain.id);

      if (domainsTenant) {
        const refreshed = await tenantControlApi.getTenant(domainsTenant.id);
        setDomainsTenant(refreshed);
      }

      await loadTenants();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to delete domain");
    } finally {
      setSaving(false);
    }
  };

  if (!isLoaded || loading) {
    return <div className={styles.page}>{t("tenant_control_loading")}</div>;
  }

  if (!isMainTenant) {
    return (
      <div className={styles.page}>
        <div className={styles.guard}>
          {t("tenant_control_main_only")}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>{t("tenant_control")}</h1>
          <p className={styles.subtitle}>
            {t("tenant_control_description")}
          </p>
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={loadTenants}
            disabled={loading}
          >
            {t("refresh")}
          </button>
          <button type="button" className={styles.primaryBtn} onClick={openCreate}>
            {t("tenant_create")}
          </button>
        </div>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <span>{summary.total}</span>
          <small>{t("tenant_total")}</small>
        </div>
        <div className={styles.summaryCard}>
          <span>{summary.plans}</span>
          <small>{t("tenant_plans")}</small>
        </div>
        <div className={styles.summaryCard}>
          <span>{summary.presets}</span>
          <small>{t("tenant_business_presets")}</small>
        </div>
        <div className={styles.summaryCard}>
          <span>{summary.activeDomains}</span>
          <small>{t("tenant_active_domains")}</small>
        </div>
        <div className={styles.summaryCard}>
          <span>{summary.users}</span>
          <small>{t("tenant_total_users")}</small>
        </div>
      </div>

      <section className={styles.panel}>
        <div className={styles.toolbar}>
          <div>
            <h2 className={styles.sectionTitle}>{t("tenant_subscription_plans")}</h2>
            <p className={styles.sectionSubtitle}>
              {t("tenant_subscription_plans_description")}
            </p>
          </div>

          <button
            type="button"
            className={styles.primaryBtn}
            onClick={openCreatePlan}
          >
            {t("tenant_create_plan")}
          </button>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t("tenant_plan")}</th>
                <th>{t("status")}</th>
                <th>{t("tenant_price")}</th>
                <th>{t("tenant_limits")}</th>
                <th>{t("actions")}</th>
              </tr>
            </thead>

            <tbody>
              {plans.length === 0 ? (
                <tr>
                  <td colSpan="5">
                    <div className={styles.empty}>{t("tenant_no_plans")}</div>
                  </td>
                </tr>
              ) : (
                plans.map((plan) => {
                  const limits = getPlanLimits(plan);

                  return (
                    <tr key={plan.id || plan.code}>
                      <td className={styles.nameCell}>
                        <strong>{plan.name}</strong>
                        <small>{plan.code}</small>
                      </td>

                      <td>
                        <span
                          className={`${styles.planBadge} ${
                            plan.is_active ? styles.mainBadge : ""
                          }`}
                        >
                          {plan.is_active ? "active" : "inactive"}
                        </span>
                      </td>

                      <td>
                        {Number(plan.monthly_price_cents || 0) / 100}{" "}
                        {plan.currency || "EUR"}
                      </td>

                      <td>
                        <div className={styles.statsGrid}>
                          <div className={styles.statMini}>
                            <strong>{limits.max_users ?? "∞"}</strong>
                            <small>{t("users")}</small>
                          </div>
                          <div className={styles.statMini}>
                            <strong>{limits.max_contacts ?? "∞"}</strong>
                            <small>{t("sales_contacts")}</small>
                          </div>
                          <div className={styles.statMini}>
                            <strong>{limits.max_leads ?? "∞"}</strong>
                            <small>{t("leads")}</small>
                          </div>
                          <div className={styles.statMini}>
                            <strong>{limits.max_deals ?? "∞"}</strong>
                            <small>{t("deals")}</small>
                          </div>
                          <div className={styles.statMini}>
                            <strong>{limits.max_tasks ?? "∞"}</strong>
                            <small>{t("tasks")}</small>
                          </div>
                          <div className={styles.statMini}>
                            <strong>{limits.max_api_keys ?? "∞"}</strong>
                            <small>{t("tenant_api_keys")}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className={styles.rowActions}>
                          <button
                            type="button"
                            className={styles.smallBtn}
                            onClick={() => openEditPlan(plan)}
                          >
                            {t("edit")}
                          </button>
                          <button
                            type="button"
                            className={styles.dangerBtn}
                            disabled={saving || plan.is_system}
                            onClick={() => handleDeletePlan(plan)}
                          >
                            {t("delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {presetResult && (
        <section className={styles.panel}>
          <div className={styles.toolbar}>
            <div>
              <h2 className={styles.sectionTitle}>{t("tenant_preset_applied")}</h2>
              <p className={styles.sectionSubtitle}>
                {presetResult.preset_name} {t("tenant_preset_applied_to")}{presetResult.tenant_id}{t("tenant_existing_data_preserved")}
              </p>
            </div>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => setPresetResult(null)}
            >
              {t("hide")}
            </button>
          </div>

          <div className={styles.statsGrid}>
            <div className={styles.statMini}>
              <strong>{presetResult.created_pipeline_stages || 0}</strong>
              <small>{t("activity_entity_pipeline_stages")}</small>
            </div>
            <div className={styles.statMini}>
              <strong>{presetResult.created_sources || 0}</strong>
              <small>{t("sales_sources")}</small>
            </div>
            <div className={styles.statMini}>
              <strong>{presetResult.created_categories || 0}</strong>
              <small>{t("categories")}</small>
            </div>
            <div className={styles.statMini}>
              <strong>{presetResult.created_subcategories || 0}</strong>
              <small>{t("subcategories")}</small>
            </div>
            <div className={styles.statMini}>
              <strong>{presetResult.created_contact_fields || 0}</strong>
              <small>{t("crm_settings_tab_contact_fields")}</small>
            </div>
          </div>
        </section>
      )}

      <section className={styles.panel}>
        <div className={styles.toolbar}>
          <input
            className={styles.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("tenant_search_placeholder")}
          />

          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={loadTenants}
          >
            {t("tenant_refresh_list")}
          </button>
        </div>

        {filteredTenants.length === 0 ? (
          <div className={styles.empty}>{t("tenant_not_found")}</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t("account_tenant")}</th>
                  <th>{t("tenant_plan")}</th>
                  <th>{t("tenant_domains")}</th>
                  <th>{t("tenant_usage_limits")}</th>
                  <th>{t("tenant_business_preset")}</th>
                  <th>{t("created")}</th>
                  <th>{t("actions")}</th>
                </tr>
              </thead>

              <tbody>
                {filteredTenants.map((tenant) => {
                  const planCode = normalizePlan(tenant.plan);
                  const planObj = tenant.subscription_plan;
                  const limits = getPlanLimits(planObj);
                  const stats = statsByTenant[tenant.id] || emptyStats(tenant.id);

                  return (
                    <tr key={tenant.id}>
                      <td className={styles.nameCell}>
                        <strong>{tenant.name}</strong>
                        <small>ID: {tenant.id}</small>
                      </td>

                      <td>
                        <span
                          className={`${styles.planBadge} ${
                            planCode === "main" ? styles.mainBadge : ""
                          }`}
                        >
                          {getPlanName(planObj) || planCode || "—"}
                        </span>
                      </td>

                      <td>
                        <div className={styles.domainList}>
                          {(tenant.domains || []).length === 0 ? (
                            <span className={styles.domainPill}>{t("tenant_no_domains")}</span>
                          ) : (
                            tenant.domains.map((domain) => (
                              <span
                                key={domain.id}
                                className={`${styles.domainPill} ${
                                  domain.is_primary ? styles.domainPrimary : ""
                                }`}
                                title={domain.is_active ? "Active" : "Inactive"}
                              >
                                {domain.domain}
                                {domain.is_primary ? " • primary" : ""}
                              </span>
                            ))
                          )}
                        </div>
                      </td>

                      <td>
                        <div className={styles.statsGrid}>
                          <div className={styles.statMini}>
                            <strong>
                              {limitText(stats.users_count, limits.max_users)}
                            </strong>
                            <small>{t("users")}</small>
                          </div>
                          <div className={styles.statMini}>
                            <strong>
                              {limitText(stats.contacts_count, limits.max_contacts)}
                            </strong>
                            <small>{t("sales_contacts")}</small>
                          </div>
                          <div className={styles.statMini}>
                            <strong>
                              {limitText(stats.leads_count, limits.max_leads)}
                            </strong>
                            <small>{t("leads")}</small>
                          </div>
                          <div className={styles.statMini}>
                            <strong>
                              {limitText(stats.deals_count, limits.max_deals)}
                            </strong>
                            <small>{t("deals")}</small>
                          </div>
                          <div className={styles.statMini}>
                            <strong>
                              {limitText(stats.tasks_count, limits.max_tasks)}
                            </strong>
                            <small>{t("tasks")}</small>
                          </div>
                          <div className={styles.statMini}>
                            <strong>
                              {limitText(stats.api_keys_count, limits.max_api_keys)}
                            </strong>
                            <small>{t("tenant_api_keys")}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className={styles.presetCell}>
                          <div className={styles.currentPresetBox}>
                            <span>{t("tenant_current")}</span>
                            <strong>
                              {translatePresetName(
                                t,
                                businessPresets.find(
                                  (item) =>
                                    item.key ===
                                    (tenant?.business_preset?.key || "generic")
                                ) || tenant?.business_preset
                              )}
                            </strong>
                          </div>

                          <select
                            value={presetSelections[tenant.id] || "generic"}
                            onChange={(e) => handlePresetChange(tenant.id, e.target.value)}
                            disabled={applyingPresetTenantId === tenant.id}
                          >
                            {businessPresets.length === 0 ? (
                              <option value="generic">
                                {t("business_preset_generic_name", {
                                  defaultValue: "Generic CRM",
                                })}
                              </option>
                            ) : (
                              businessPresets.map((preset) => (
                                <option key={preset.key} value={preset.key}>
                                  {translatePresetName(t, preset)}
                                </option>
                              ))
                            )}
                          </select>

                          <button
                            type="button"
                            className={styles.primaryBtn}
                            disabled={
                              applyingPresetTenantId === tenant.id ||
                              (presetSelections[tenant.id] || "generic") ===
                                (tenant?.business_preset?.key || "generic")
                            }
                            onClick={() => handleApplyBusinessPreset(tenant)}
                          >
                            {applyingPresetTenantId === tenant.id
                              ? "Applying..."
                              : (presetSelections[tenant.id] || "generic") ===
                                (tenant?.business_preset?.key || "generic")
                              ? "Applied"
                              : "Apply"}
                          </button>
                        </div>
                      </td>

                      <td>{formatDate(tenant.created_at)}</td>

                      <td>
                        <div className={styles.rowActions}>
                          <button
                            type="button"
                            className={styles.smallBtn}
                            onClick={() => openEdit(tenant)}
                          >
                            {t("edit")}
                          </button>
                          <button
                            type="button"
                            className={styles.smallBtn}
                            onClick={() => openDomains(tenant)}
                          >
                            {t("tenant_domains")}
                          </button>
                          <button
                            type="button"
                            className={styles.smallBtn}
                            onClick={loadTenants}
                          >
                            {t("refresh")}
                          </button>
                          <button
                            type="button"
                            className={styles.dangerBtn}
                            disabled={planCode === "main"}
                            onClick={() => handleDelete(tenant)}
                          >
                            {t("delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {(createOpen || editTenant) && (
        <div className={styles.modalBackdrop} onMouseDown={closeModal}>
          <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <div>
                <h3>{editTenant ? "Edit Tenant" : "Create Tenant"}</h3>
                <p>
                  {editTenant
                    ? "Update tenant name and subscription plan."
                    : "Create a new tenant and optionally attach a primary domain."}
                </p>
              </div>

              <button type="button" className={styles.smallBtn} onClick={closeModal}>
                {t("close")}
              </button>
            </div>

            <div className={styles.formGrid}>
              <label className={styles.field}>
                <span>{t("tenant_name")}</span>
                <input
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder={t("tenant_name_placeholder")}
                />
              </label>

              <label className={styles.field}>
                <span>{t("tenant_subscription_plan")}</span>
                <select
                  value={form.plan_id}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, plan_id: e.target.value }))
                  }
                >
                  <option value="">{t("tenant_no_plan")}</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} ({plan.code})
                    </option>
                  ))}
                </select>
              </label>

              {!editTenant && (
                <label className={styles.field}>
                  <span>{t("tenant_primary_domain_optional")}</span>
                  <input
                    value={form.domain}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, domain: e.target.value }))
                    }
                    placeholder="client.your-domain.com"
                  />
                </label>
              )}
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={closeModal}
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                disabled={saving}
                onClick={editTenant ? handleUpdate : handleCreate}
              >
                {saving ? "Saving..." : editTenant ? "Save changes" : "Create tenant"}
              </button>
            </div>
          </div>
        </div>
      )}

      {planModalOpen && (
        <div className={styles.modalBackdrop} onMouseDown={closeModal}>
          <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <div>
                <h3>{editPlan ? "Edit Plan" : "Create Plan"}</h3>
                <p>
                  {t("tenant_empty_limit_hint")}
                </p>
              </div>

              <button type="button" className={styles.smallBtn} onClick={closeModal}>
                {t("close")}
              </button>
            </div>

            <div className={styles.formGrid}>
              <label className={styles.field}>
                <span>{t("tenant_plan_name")}</span>
                <input
                  value={planForm.name}
                  onChange={(e) =>
                    setPlanForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Starter"
                />
              </label>

              <label className={styles.field}>
                <span>{t("tenant_plan_code")}</span>
                <input
                  value={planForm.code}
                  disabled={editPlan?.is_system}
                  onChange={(e) =>
                    setPlanForm((prev) => ({ ...prev, code: e.target.value }))
                  }
                  placeholder="starter"
                />
              </label>

              <label className={styles.field}>
                <span>{t("description")}</span>
                <input
                  value={planForm.description}
                  onChange={(e) =>
                    setPlanForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder={t("tenant_plan_description_placeholder")}
                />
              </label>

              <label className={styles.field}>
                <span>{t("tenant_monthly_price_cents")}</span>
                <input
                  type="number"
                  min="0"
                  value={planForm.monthly_price_cents}
                  onChange={(e) =>
                    setPlanForm((prev) => ({
                      ...prev,
                      monthly_price_cents: e.target.value,
                    }))
                  }
                />
              </label>

              <label className={styles.field}>
                <span>{t("crm_settings_currency_label")}</span>
                <input
                  value={planForm.currency}
                  onChange={(e) =>
                    setPlanForm((prev) => ({ ...prev, currency: e.target.value }))
                  }
                  placeholder={t("crm_settings_currency_eur")}
                />
              </label>

              <label className={styles.checkLabel}>
                <input
                  type="checkbox"
                  checked={planForm.is_active}
                  onChange={(e) =>
                    setPlanForm((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                />
                {t("tenant_active_plan")}
              </label>

              {[
                ["max_users", "Max users"],
                ["max_contacts", "Max contacts"],
                ["max_leads", "Max leads"],
                ["max_deals", "Max deals"],
                ["max_tasks", "Max tasks"],
                ["max_api_keys", "Max API keys"],
              ].map(([key, label]) => (
                <label key={key} className={styles.field}>
                  <span>{label}</span>
                  <input
                    type="number"
                    min="0"
                    value={planForm.limits_json[key]}
                    onChange={(e) =>
                      setPlanForm((prev) => ({
                        ...prev,
                        limits_json: {
                          ...prev.limits_json,
                          [key]: e.target.value,
                        },
                      }))
                    }
                    placeholder={t("tenant_unlimited_placeholder")}
                  />
                </label>
              ))}

              <label className={styles.checkLabel}>
                <input
                  type="checkbox"
                  checked={!!planForm.limits_json.external_api_enabled}
                  onChange={(e) =>
                    setPlanForm((prev) => ({
                      ...prev,
                      limits_json: {
                        ...prev.limits_json,
                        external_api_enabled: e.target.checked,
                      },
                    }))
                  }
                />
                {t("tenant_external_api_enabled")}
              </label>

              <label className={styles.checkLabel}>
                <input
                  type="checkbox"
                  checked={!!planForm.limits_json.ai_enabled}
                  onChange={(e) =>
                    setPlanForm((prev) => ({
                      ...prev,
                      limits_json: {
                        ...prev.limits_json,
                        ai_enabled: e.target.checked,
                      },
                    }))
                  }
                />
                {t("tenant_ai_enabled")}
              </label>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={closeModal}
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                className={styles.primaryBtn}
                disabled={saving}
                onClick={handleSavePlan}
              >
                {saving ? "Saving..." : editPlan ? "Save plan" : "Create plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {domainsTenant && (
        <div className={styles.modalBackdrop} onMouseDown={closeModal}>
          <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
            <div className={styles.modalHead}>
              <div>
                <h3>{t("tenant_domains_for")} {domainsTenant.name}</h3>
                <p>{t("tenant_domains_description")}</p>
              </div>

              <button type="button" className={styles.smallBtn} onClick={closeModal}>
                {t("close")}
              </button>
            </div>

            <div className={styles.domainManager}>
              <div className={styles.addDomainRow}>
                <input
                  value={domainForm.domain}
                  onChange={(e) =>
                    setDomainForm((prev) => ({
                      ...prev,
                      domain: e.target.value,
                    }))
                  }
                  placeholder="tenant.example.com"
                />

                <label className={styles.checkLabel}>
                  <input
                    type="checkbox"
                    checked={domainForm.is_primary}
                    onChange={(e) =>
                      setDomainForm((prev) => ({
                        ...prev,
                        is_primary: e.target.checked,
                      }))
                    }
                  />
                  {t("tenant_primary")}
                </label>

                <button
                  type="button"
                  className={styles.primaryBtn}
                  disabled={saving}
                  onClick={handleAddDomain}
                >
                  {t("tenant_add_domain")}
                </button>
              </div>

              <div className={styles.domainRows}>
                {(domainsTenant.domains || []).length === 0 ? (
                  <div className={styles.empty}>{t("tenant_no_attached_domains")}</div>
                ) : (
                  domainsTenant.domains.map((domain) => (
                    <div key={domain.id} className={styles.domainRow}>
                      <code>{domain.domain}</code>
                      <span
                        className={`${styles.planBadge} ${
                          domain.is_primary ? styles.mainBadge : ""
                        }`}
                      >
                        {domain.is_primary ? "primary" : "secondary"}
                      </span>
                      <button
                        type="button"
                        className={styles.dangerBtn}
                        disabled={saving}
                        onClick={() => handleDeleteDomain(domain)}
                      >
                        {t("delete")}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
